import { useFieldArray, useForm } from "react-hook-form";
import EMRSectionSearch from "@/app/components/ui/Reusable/EMRSectionSearch";
import {
  EMRdetailsProps,
  IEMRSectionSearchResponseData,
  IEMRUpdatePayloadProps,
} from "@/api_framework/api_modals/EMRs";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import Input from "@/app/components/ui/Inputs/Input";
import { debounce, toNumber } from "lodash";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import { useEffect, useMemo, useRef, useState } from "react";
import EMRItemsSuggestionsComponent from "../EMRItemsSuggestionsComponent";
import VaccinationAddOnsComponent from "../VaccinationAddOnsComponent";
import EMRDeleteReasonModal from "../EMRDeleteReasonModal";
import { AnimatePresence, motion } from "motion/react";
import toast from "react-hot-toast";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { LOCKED_CHILD_ITEM_IDS } from "@/constants";

type TreatmentSectionFormFieldType = {
  treatment_id: string;
  name: string;
  remark: string;
  quantity: number;
  item_tag?: string;
  text_tag?: string;
  is_child?: boolean;
};
export interface IEMRTreatmentSectionFormFields {
  treatments: TreatmentSectionFormFieldType[];
  instructions: string;
}

const isLockedChildTreatmentItem = (item?: Partial<TreatmentSectionFormFieldType>) =>
  Boolean(item?.is_child && item?.treatment_id && LOCKED_CHILD_ITEM_IDS.includes(item.treatment_id));

const TreatmentsComponent = ({ emrDetails, setEmrDetails }: EMRdetailsProps) => {
  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();
  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  const [animateLast, setAnimateLast] = useState(false);
  const [invalidQuantities, setInvalidQuantities] = useState<Record<number, boolean>>({});
  const [pendingDeleteItem, setPendingDeleteItem] = useState<TreatmentSectionFormFieldType | null>(null);

  const { execute: logAPI } = useClickLogAPI();

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");
  const emr_status = params.get("emr_status");

  const customerId = emrDetails?.customer_details?.customer_id;
  const pet_id = emrDetails?.pet_details?.pet_id;
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  // RBAC: EMR_TEMPLATE_DELETE_LOCKED_ITEM — legacy role: super_admin
  const canDeleteLockedItem = useFeatureAccess("EMR_TEMPLATE_DELETE_LOCKED_ITEM");

  const formHook = useForm<IEMRTreatmentSectionFormFields>({
    mode: "onChange",
  });

  const treatmentFormHook = useFieldArray({
    name: "treatments",
    control: formHook.control,
  });

  const isFirstLoad = useRef(true);

  // True while the user has local edits that are not yet persisted. Set by
  // scheduleAutoSave/watch, cleared when the debounced save actually runs or
  // when a direct API call sends the current form values anyway.
  const pendingSaveRef = useRef(false);

  // Debounced API function

  const debouncedUpdate = useMemo(
    () =>
      debounce((data) => {
        pendingSaveRef.current = false;
        const payload: IEMRUpdatePayloadProps = {
          treatments: {
            treatments_added: data.treatments,
            treatments_instructions: data.instructions,
          },
          remove_children: emrDetails?.remove_children,
          emr_status: emrDetails?.emr_status ?? emr_status,
        };
        if (JSON.stringify(data) === JSON.stringify(emrDetails?.treatments)) return;
        updateEmrAPI(emr_id, payload);
      }, 1000),
    [emr_id, emrDetails?.emr_status, emrDetails?.treatments],
  );

  // Quantity steppers/inputs mutate the form via setValue, which RHF reports
  // with type undefined — the watch filter below ignores those. Every
  // user-driven programmatic mutation must call this to persist.
  const scheduleAutoSave = () => {
    pendingSaveRef.current = true;
    debouncedUpdate(formHook.getValues());
  };

  // The single guarded watch subscription below handles auto-save. A prior
  // implementation added another handleInputChange() that spun up a fresh
  // watch subscription on every keystroke without ever unsubscribing — those
  // orphaned subscriptions piled up and duplicated every subsequent API call.

  useEffect(() => {
    // Only auto-save on ACTUAL user input. RHF's watch callback signature is
    // (value, { name, type }) — `type` is defined ('change', 'blur', etc.) for
    // user events and undefined for programmatic setValue/reset/replace. Any
    // isFirstLoad timing dance is fragile because RHF doesn't guarantee that
    // the subscription callback fires synchronously with replace(); by the
    // time it fires, our setTimeout(0) has already flipped the flag back on.
    // Filtering on `type === "change"` is the deterministic fix.
    const subscription = formHook.watch((value, { type }) => {
      if (type !== "change") return;
      pendingSaveRef.current = true;
      debouncedUpdate(value as IEMRTreatmentSectionFormFields);
    });

    return () => subscription.unsubscribe();
  }, [formHook, debouncedUpdate]);

  const handleAddTreatment = async (item: IEMRSectionSearchResponseData, source?: string) => {
    const existing = formHook.getValues("treatments");
    const exists = treatmentFormHook.fields.some((f) => f.name === item.name);
    if (exists) {
      toast("This Test already added.");
      return;
    }
    // Payload below includes pending edits via getValues(); cancel only after
    // the duplicate early-return so a rejected add keeps the pending save.
    debouncedUpdate.cancel();
    pendingSaveRef.current = false;

    const newItem = {
      name: item.name,
      remark: "",
      quantity: 1,
      treatment_id: item?.vetic_variant_id,
      item_tag: item?.item_tag,
      coupon_id: (item as any)?.coupon_id,
    };
    setAnimateLast(true);

    const addedList = [...existing, newItem];

    const payload: IEMRUpdatePayloadProps = {
      treatments: {
        treatments_added: addedList,
        treatments_instructions: formHook.getValues("instructions"),
      },
      remove_children: emrDetails?.remove_children,
      emr_status: emrDetails?.emr_status ?? emr_status,
    };

    // Step 3: Call Update API
    await updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        setEmrDetails(message);
      }
    });
    source && callLogSuggestionAPI(item);
  };

  useEffect(() => {
    if (emrDetails) {
      // The user has unsaved local edits (e.g. a quantity change) and another
      // section's save just refreshed emrDetails with data that predates them.
      // Persist the local edits NOW and keep the form as-is — replacing it
      // here would silently reset quantities back to the stale server values.
      if (pendingSaveRef.current) {
        debouncedUpdate.flush();
        return;
      }

      isFirstLoad.current = false;
      // Cancel any pending debounced update. Without this, a debounced call
      // that was scheduled from the user's own typing will still fire AFTER
      // the reset below, pushing stale form values back to the server.
      debouncedUpdate.cancel();

      const treatmentItems = emrDetails?.treatments?.treatments_added || [];
      treatmentFormHook.replace(
        treatmentItems.map((item) => {
          if (isLockedChildTreatmentItem(item) && toNumber(item?.quantity) <= 0) {
            return {
              ...item,
              quantity: 1,
            };
          }
          return item;
        }),
      );
      formHook.setValue("instructions", emrDetails?.treatments?.treatments_instructions || "", {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });

      setTimeout(() => {
        isFirstLoad.current = true;
      }, 0);
    }
  }, [emrDetails]);

  const isChilderRemovefromEMR = (index: number, item: any, reason?: string) => {
    // Payload below is built from getValues(), so any pending debounced edits
    // are already included — drop the pending save so the response replace runs.
    debouncedUpdate.cancel();
    pendingSaveRef.current = false;
    treatmentFormHook.remove(index);
    const existing = formHook.getValues("treatments");
    let remove_children = null;
    if (item?.is_child) {
      remove_children = [...(emrDetails?.remove_children ?? []), item?.treatment_id];
    } else {
      remove_children = [...(emrDetails?.remove_children ?? [])];
    }

    const payload: IEMRUpdatePayloadProps = {
      treatments: {
        treatments_added: existing,
        treatments_instructions: formHook.getValues("instructions"),
      },
      remove_children: remove_children,
      emr_status: emrDetails?.emr_status ?? emr_status,
    };

    updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        setEmrDetails(message);
      }
    });
    callLogAPILabDeleted(item?.treatment_id, reason);
  };

  const handleConfirmTaggedDelete = (reason: string) => {
    const index = (formHook.getValues("treatments") ?? []).findIndex(
      (treatment) => treatment?.treatment_id === pendingDeleteItem?.treatment_id,
    );
    if (index !== -1) {
      isChilderRemovefromEMR(index, pendingDeleteItem, reason);
    }
    setPendingDeleteItem(null);
  };

  const handleDeleteVaccineAddon = (vaccineId: any) => {
    const remaining = (emrDetails?.vaccination_add_ons || []).filter((a) => a?.vaccine_id !== vaccineId);
    const payload: IEMRUpdatePayloadProps = {
      vaccination_add_ons: remaining,
      deleted_vaccine_addon_id: vaccineId,
      emr_status: emrDetails?.emr_status ?? emr_status,
    };
    updateEmrAPI(emr_id, payload).then(({ status }) => {
      if (status) {
        setEmrDetails({ ...emrDetails, vaccination_add_ons: remaining });
      }
    });
  };

  useEffect(() => {
    if (!animateLast) return;
    const t = setTimeout(() => setAnimateLast(false), 600);
    return () => clearTimeout(t);
  }, [animateLast, emrDetails]);

  const callLogAPIBase = (
    item: string,
    eventType: "EMR_QUANTITY_PLUS" | "EMR_QUANTITY_MINUS" | "EMR_ITEM_DELETED" | "SUGGESTION_ITEMS",
    reason?: string,
  ) => {
    const fieldValue = formHook.watch("treatments");

    const logBody: IClickLogBody = {
      event_type: eventType,
      scr_id: "EMR",
      client_id: customerId,
      emr_id: emr_id,
      pet_id: pet_id,
      click_on_search_item: item || undefined,
      treatments_section: fieldValue || undefined,
      event_action: reason,
      clinic_id: mappedClinicId,
      emr_search_type: "Treatment",
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
    };

    logAPI(logBody);
  };

  // Usage
  const callLogAPILab = (item: any) => callLogAPIBase(item, "EMR_QUANTITY_PLUS");
  const callLogSuggestionAPI = (item: any) => callLogAPIBase(item, "SUGGESTION_ITEMS");
  const callLogAPILabMinus = (item: any) => callLogAPIBase(item, "EMR_QUANTITY_MINUS");

  const callLogAPILabDeleted = (item: any, reason?: string) => {
    callLogAPIBase(item, "EMR_ITEM_DELETED", reason);
  };

  const watchedTreatments = formHook.watch("treatments") || [];

  return (
    <div className=" w-full py-3 bg-white p-3 rounded-[10px] mt-3">
      <div className="bg-[#F9FAFB] rounded-xl p-2  ">
        <div className="flex flex-col gap-2 pb-2 sm:flex-row sm:gap-10 sm:items-center">
          <h3 className="shrink-0 text-[#000000] text-sm font-medium tracking-tight42 sm:w-2/12">Treatments</h3>
          <div className="w-full sm:flex-1 sm:-mt-1">
            <EMRSectionSearch
              clinicId={mappedClinicId}
              emrType={emrDetails?.emr_type}
              onSelectItem={(item) => {
                handleAddTreatment(item);
              }}
              placeholder="Search & Add Treatments"
              containerClassName="w-full sm:max-w-[600px]"
              itemActiveClassName="bg-[#f6f6f6] text-black"
              inputContainerClassName="w-full"
              flexibleAdd={false}
              searchType="Treatment"
            />
          </div>
        </div>
        <div className={treatmentFormHook?.fields?.length > 0 ? "bg-white p-[10px] rounded-lg" : ""}>
          {treatmentFormHook?.fields?.length > 0 && (
            <div className="rounded-xl overflow-x-auto">
              <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] rounded-md font-normal ">
                  <tr>
                  <th className="p-3 pl-2 w-1/2 font-medium text-left text-[10px] sm:text-xs">Item Names</th>
                  <th className="p-3 pl-2 w-1/4 font-medium text-center text-[10px] sm:text-xs">Quantity</th>
                  <th className="font-medium  w-1/4 text-center text-[10px] sm:text-xs ">Remarks</th>
                  <th className="font-medium w-1/4 text-center text-[10px] sm:text-xs pr-2">Actions</th>
                </tr>
                </thead>

                <tbody className="overflow-hidden  rounded-xl w-full">
                  <AnimatePresence>
                    {treatmentFormHook?.fields?.map((field, index) => {
                      const isFirst = index === 0;
                      const isLast = index === treatmentFormHook?.fields?.length - 1;
                      const isSingle = treatmentFormHook?.fields?.length === 1;
                      const isLockedChildItem = isLockedChildTreatmentItem(field);
                      const isDeleteDisabled = isLockedChildItem && !canDeleteLockedItem;
                      // stable key: prefer medicine_id (persisted id) or fallback to field.id
                      const rowKey = field.treatment_id ?? field.id ?? `${index}`;

                      // only the last row will use initial animation when animateLast is true
                      const initialProp = animateLast && isLast ? { opacity: 0, y: 10, scale: 0.97 } : undefined; // undefined = no initial mount animation

                      return (
                        <motion.tr
                          key={rowKey}
                          initial={initialProp}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.97 }}
                          // transition={{ duration: 0.28 }}
                          transition={{ duration: 0.45, ease: "easeInOut" }}
                          className={`
    bg-white overflow-hidden
    ${isSingle ? "rounded-bl-xl" : ""}
    ${isFirst && !isSingle ? "rounded-t-xl" : ""}
    ${isLast && !isSingle ? "rounded-bl-xl" : ""}
  `}
                        >
                          <td
                            className={`p-2 text-left ${
                              isSingle
                                ? "rounded-tl-xl rounded-bl-xl"
                                : `${isFirst ? "rounded-tl-xl" : ""} ${isLast ? "rounded-b-xl" : ""}`.trim()
                            }`}
                          >
                            <input hidden {...formHook.register(`treatments.${index}.name`)} />
                            <span className="text-[10px] sm:text-xs text-[#1F1F23] break-words font-normal">
                              {formHook.watch(`treatments.${index}.name`)}
                            </span>
                            {field?.text_tag && (
                              <div>
                                <span className="inline-flex items-center rounded px-2 py-0.5 mt-1 text-[10px] font-semibold text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-600/20">
                                  {field.text_tag}
                                </span>
                              </div>
                            )}
                            {field?.is_child && (
                              <p className="items-center rounded-md  px-2 py-1 text-[10px] font-semibold w-[70px] ml-2  ring-1 ring-inset text-gray-500 bg-blue-50 ring-green-500/10 my-2">
                                Suggested
                              </p>
                            )}
                          </td>
                          <td className="p-2 text-center align-middle">
                            <div className="w-32 mx-auto">
                              <InputWithStartAndEndAddOn
                                startAddon="-"
                                startAddOnCallback={() => {
                                  const currentQuantity = toNumber(formHook.watch(`treatments.${index}.quantity`));
                                  if (isLockedChildItem && currentQuantity <= 1) {
                                    formHook.setValue(`treatments.${index}.quantity`, 1);
                                  } else if (currentQuantity !== 1) {
                                    formHook.setValue(
                                      `treatments.${index}.quantity`,
                                      currentQuantity - 1,
                                    );
                                  }
                                  scheduleAutoSave();
                                  callLogAPILabMinus(formHook.watch(`treatments.${index}.treatment_id`));
                                }}
                                step={1}
                                endAddOnCallback={() => {
                                  formHook.setValue(
                                    `treatments.${index}.quantity`,
                                    formHook.watch(`treatments.${index}.quantity`) + 1,
                                  );
                                  scheduleAutoSave();
                                  callLogAPILab(formHook.watch(`treatments.${index}.treatment_id`));
                                }}
                                min={1}
                                endAddon="+"
                                data-emr-quantity="true"
                                data-invalid-quantity={invalidQuantities[index] ? "true" : "false"}
                                value={formHook.watch(`treatments.${index}.quantity`)}
                                inputClassName={invalidQuantities[index] ? "!ring-red-500 !text-red-500" : ""}
                                onChange={(e: any) => {
                                  const rawValue = e.target.value;
                                  if (rawValue === "") {
                                    formHook.setValue(`treatments.${index}.quantity`, "" as any);
                                    setInvalidQuantities((prev) => ({ ...prev, [index]: false }));
                                    return;
                                  }
                                  const parsedQuantity = toNumber(rawValue);
                                  if (!Number.isNaN(parsedQuantity)) {
                                    const safeQuantity = isLockedChildItem && parsedQuantity <= 0 ? 1 : parsedQuantity;
                                    formHook.setValue(`treatments.${index}.quantity`, safeQuantity as any);
                                    setInvalidQuantities((prev) => ({
                                      ...prev,
                                      [index]: safeQuantity < 0 || !Number.isInteger(safeQuantity),
                                    }));
                                    if (safeQuantity > 0 && Number.isInteger(safeQuantity)) {
                                      scheduleAutoSave();
                                    }
                                  }
                                  callLogAPILab(formHook.watch(`treatments.${index}.treatment_id`));
                                }}
                                onBlur={() => {
                                  const currentValue: any = formHook.getValues(`treatments.${index}.quantity`);
                                  const parsedQuantity = toNumber(currentValue);

                                  if (currentValue === "" || Number.isNaN(parsedQuantity)) {
                                    formHook.setValue(`treatments.${index}.quantity`, 1);
                                    setInvalidQuantities((prev) => ({ ...prev, [index]: false }));
                                    scheduleAutoSave();
                                    return;
                                  }

                                  if (parsedQuantity <= 0) {
                                    formHook.setValue(`treatments.${index}.quantity`, 1);
                                    setInvalidQuantities((prev) => ({ ...prev, [index]: false }));
                                    scheduleAutoSave();
                                    if (!isLockedChildItem) {
                                      toast.error("Decimal, Zero and Negative values are not allowed !");
                                    }
                                    return;
                                  }

                                  if (!Number.isInteger(parsedQuantity)) {
                                    setInvalidQuantities((prev) => ({ ...prev, [index]: true }));
                                    toast.error("Decimal, Zero and Negative values are not allowed !");
                                    return;
                                  }

                                  setInvalidQuantities((prev) => ({ ...prev, [index]: false }));
                                }}
                                type="number"
                              />
                            </div>
                          </td>
                          <td className="p-2">
                            <div className="pt-2 pb-2 w-full">
                              <Input
                                errorMessages={[{ message: "Temperature is required.", type: "required" }]}
                                fieldError={formHook.formState.errors.treatments}
                                register={formHook.register(`treatments.${index}.remark`, {
                                  required: false,
                                })}
                                inputClassName="text-xs h-9"
                                type="text"
                                placeholder="Remarks"
                              />
                            </div>
                          </td>

                          <td
                            className={`p-2 text-center ${
                              isSingle
                                ? "rounded-tr-xl rounded-br-xl"
                                : `${isFirst ? "rounded-tr-xl" : ""} ${isLast ? "rounded-br-xl" : ""}`.trim()
                            }`}
                          >
                            <button
                              className={`w-8 h-8 flex items-center justify-center bg-[#F2F2F2] rounded-[10px] ${
                                isDeleteDisabled ? "opacity-50 cursor-not-allowed" : ""
                              }`}
                              disabled={isDeleteDisabled}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isDeleteDisabled) {
                                  return;
                                }
                                if (field?.text_tag) {
                                  setPendingDeleteItem(field);
                                  return;
                                }
                                isChilderRemovefromEMR(index, field);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="white"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="size-5 text-[#ED0101]"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                />
                              </svg>
                            </button>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
          <EMRItemsSuggestionsComponent
            handelCallback={(item, s) => handleAddTreatment(item, s)}
            suggestionData={emrDetails?.treatments?.treatments_suggestions}
          />
        </div>

        <VaccinationAddOnsComponent
          vaccinationAddOns={emrDetails?.vaccination_add_ons}
          onAddItem={(item, source) => handleAddTreatment(item, source)}
          customerId={customerId}
          petId={pet_id}
          emrId={emr_id}
          petName={emrDetails?.pet_details?.pet_name}
          addedTreatments={watchedTreatments}
          onDeleteAddon={handleDeleteVaccineAddon}
        />

        {pendingDeleteItem && (
          <EMRDeleteReasonModal
            title="Delete Treatment"
            itemName={pendingDeleteItem.name}
            variant="treatment"
            onConfirm={handleConfirmTaggedDelete}
            onClose={() => setPendingDeleteItem(null)}
          />
        )}

        <div className="bg-[#FFFFFF] rounded-xl p-[10px]  mt-2">
          <textarea
            className="w-full   border border-[#E2E2E2] rounded-xl p-3 text-sm text-[#6C727F] focus:outline-none"
            placeholder="Additional Treatment Instructions"
            {...formHook.register("instructions", {
              required: false,
            })}
          ></textarea>
        </div>
      </div>
    </div>
  );
};
export default TreatmentsComponent;
